import { Component, inject, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { IonToast, ToastButton } from '@ionic/angular';
import { Store } from '@ngrx/store';

import { NinetyToastOptions } from '@ninety/mobile/platform/state';

@Component({
  selector: 'nty-toast',
  // changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonToast],
  template: `
    <ion-toast
      [isOpen]="isToastOpen"
      [message]="options?.message"
      [buttons]="_toastButtons"
      [cssClass]="_classes"
      icon="/assets/icons/{{ _icon }}.svg"
      [duration]="options?.duration || 5000"
      (didDismiss)="setOpen(false)" />
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: `
    @use 'terra';

    .nty-toast {
      --border-radius: #{terra.radius(small)};
      --color: #{terra.color(text-invert)};

      &::part(icon) {
        font-size: 20px !important;
      }

      &::part(button) {
        color: terra.color(text-invert, light);
        font-size: 14px;
        padding: 0;
      }

      &::part(button cancel) {
        color: terra.color(text-invert, light);
        font-size: 14.3px; // This is the only way to target the nested shadow dom which uses EMs
        padding-inline: 16px;
      }

      &--type {
        &-positive {
          --background: #{terra.color(green, heavy)};
        }

        &-positive-weak {
          --background: #{terra.color(neutral-heavy, 500)};

          &::part(icon) {
            color: terra.color(green);
          }
        }

        &-neutral {
          --background: #{terra.color(neutral-heavy, 500)};

          &::part(icon) {
            color: terra.color(text-invert, light);
          }
        }

        &-negative-weak {
          --background: #{terra.color(neutral-heavy, 500)};

          &::part(icon) {
            color: terra.color(red);
          }
        }

        &-negative {
          --background: #{terra.color(red, heavy)};

          &::part(icon) {
            color: terra.color(text-invert);
          }
        }
      }
    }
  `,
})
export class ToastComponent implements OnChanges {
  isToastOpen = false;

  private readonly store = inject(Store);

  private _toastLearnMoreButton: ToastButton = {
    text: 'View',
    side: 'end',
    handler: () => {
      if (this.options?.action?.callback) {
        this.options.action.callback();
      }
      if (this.options?.action?.dispatchAction) {
        this.store.dispatch(this.options.action.dispatchAction);
      }
    },
  };

  private _toastDismissButton: ToastButton = {
    icon: '/assets/icons/x--bold.svg',
    side: 'end',
    role: 'cancel',
  };

  protected _toastButtons = [this._toastDismissButton];
  protected _classes = ['nty-toast'];
  protected _icon = 'check-circle--fill';

  @Input() options: NinetyToastOptions | null = null;

  ngOnChanges(changes: SimpleChanges) {
    if (!this.options || !this.options?.message) return;

    this._classes = ['nty-toast', `nty-toast--type-${this.options.type || 'positive'}`];

    this._toastButtons = [this._toastDismissButton];
    if (this.options.action) {
      this._toastButtons.unshift(this._toastLearnMoreButton);
      if (this.options.action.text) {
        this._toastButtons[0].text = this.options.action.text;
      }
    }
    switch (this.options.type) {
      case 'positive':
      case 'positive-weak':
        this._icon = 'check-circle--fill';
        break;
      case 'neutral':
        this._icon = 'info-circle--fill';
        break;
      case 'negative-weak':
      case 'negative':
        this._icon = 'warning-triangle--fill';
        break;
    }
    this.setOpen(true);
  }

  setOpen(isOpen: boolean) {
    this.isToastOpen = isOpen;
  }
}
